/**
 * useContextStatus — poll ADK auto-compaction status for a session.
 *
 * The backend manual compact endpoint was retired; ADK now auto-compacts
 * post-invocation when prompt_token_estimate >= compaction_threshold.
 * This hook polls GET /api/v1/sessions/{id}/context-status every 5s so
 * the dashboard can show a live context-usage indicator.
 *
 *   - Stops polling when sessionId becomes null or the component unmounts.
 *   - Re-fetches immediately when sessionId changes.
 *   - Returns `{ status, loading, error, refetch }` so callers can show
 *     loading / error states and force-refresh after a user action.
 */
import * as React from "react";
import { getContextStatus, type ContextStatusOut } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { apiErrorMessage } from "@/lib/api-error";

const POLL_INTERVAL_MS = 5000;

export interface UseContextStatusResult {
  status: ContextStatusOut | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useContextStatus(sessionId: string | null): UseContextStatusResult {
  const client = useApiClient();
  const [status, setStatus] = React.useState<ContextStatusOut | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Bumping this counter forces the effect to re-run for an immediate fetch.
  const [refetchTick, setRefetchTick] = React.useState(0);
  const refetch = React.useCallback(() => {
    setRefetchTick((n) => n + 1);
  }, []);

  React.useEffect(() => {
    if (!sessionId) {
      setStatus(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchOnce = async () => {
      try {
        const res = await getContextStatus({
          client,
          path: { session_id: sessionId },
        });
        if (cancelled) return;
        if (res.error) {
          // FastAPI errors are {detail}; a string detail is the reason.
          const detail = (res.error as { detail?: unknown }).detail;
          const msg = typeof detail === "string" ? detail : "context-status failed";
          setError(msg);
        } else if (res.data) {
          setStatus(res.data);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchOnce();
    const id = window.setInterval(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionId, client, refetchTick]);

  return { status, loading, error, refetch };
}
